import { css, Global, Theme, ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router';
import { RoutesWidget } from '../../RoutesWidget';
import { LightboxWrapper } from '../LightboxWrapper';
import { DarkTheme, LightTheme } from '../theme/Theme';
import { FooterWidget } from './FooterWidget';
import { HeaderWidget } from './header/HeaderWidget';
import { LayoutClassName } from './LayoutClassName';
import { SCROLL } from '../scroll';

const githubDarkHighlightCss = require('!!raw-loader!highlight.js/styles/github-dark.css').default as string;
const githubLightHighlightCss = require('!!raw-loader!highlight.js/styles/github.css').default as string;

const normalizePathname = (pathname: string): string => {
  const collapsed = pathname.replace(/\/{2,}/g, '/');
  if (collapsed === '/') {
    return collapsed;
  }
  return collapsed.endsWith('/') ? collapsed : `${collapsed}/`;
};

export const RootWidget: React.FC = () => {
  const [dark, setDark] = useState<boolean>(() => {
    return JSON.parse(localStorage.getItem('dark') || 'true');
  });

  useEffect(() => {
    localStorage.setItem('dark', JSON.stringify(dark));
  }, [dark]);

  const toggleTheme = useCallback(() => {
    setDark((prev) => !prev);
  }, []);

  return (
    <ThemeProvider theme={dark ? DarkTheme : LightTheme}>
      <LightboxWrapper>
        <Global styles={S.Global} />
        <BrowserRouter>
          <HeaderWidget dark={dark} toggleTheme={toggleTheme} />
          <RootContentWidget />
        </BrowserRouter>
      </LightboxWrapper>
    </ThemeProvider>
  );
};

export const RootContentWidget: React.FC = () => {
  const navigation = useLocation();
  const navigate = useNavigate();
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const normalizedPathname = normalizePathname(navigation.pathname);
    if (normalizedPathname !== navigation.pathname) {
      navigate(`${normalizedPathname}${navigation.search}${navigation.hash}`, { replace: true });
    }
  }, [navigation.hash, navigation.pathname, navigation.search, navigate]);

  useEffect(() => {
    scrollableRef.current?.scrollTo({
      top: 0
    });
  }, [navigation.pathname]);

  return (
    <S.Main ref={scrollableRef} className={LayoutClassName.SCROLL_CONTAINER}>
      <RoutesWidget />
      <S.Expand />
      <S.Footer />
    </S.Main>
  );
};

namespace S {
  export const Global = (theme: Theme) => css`
    ${theme.light ? githubLightHighlightCss : githubDarkHighlightCss}

    * {
      padding: 0;
      margin: 0;
    }

    *:focus {
      outline: none;
    }

    /* Embedded SVG media queries follow the site toggle, including in the lightbox. */
    img {
      color-scheme: ${theme.light ? 'light' : 'dark'};
    }

    html,
    body,
    #application {
      height: 100dvh;
      background: ${theme.layout.background};
      font-family: ${theme.text.font}, ${theme.text.fontFallback};
      color: ${theme.text.paragraph};
      overflow: hidden;
    }

    @media (prefers-reduced-motion: reduce) {
      body {
        animation: none;
      }
    }
  `;

  export const Footer = styled(FooterWidget)`
    flex-shrink: 0;
    flex-grow: 0;
  `;

  export const Expand = styled.div`
    flex-grow: 1;
  `;

  export const Main = styled.div`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    margin-top: -${(p) => p.theme.header.height}px;
    padding-top: ${(p) => p.theme.header.height}px;
    flex-grow: 1;
    overflow: auto;
    height: 100%;
    ${(p) => SCROLL(p.theme, true)};
  `;
}
